'use client'

import type { ContentItem } from '@/types/solution'
import { Sparkles } from 'lucide-react'
import * as LucideIcons from 'lucide-react'
import { GlowCard } from '@/components/feature/ui/glow-card'
import { motion } from 'framer-motion'

const glowColors = ['purple', 'green', 'red', 'teal', 'orange', 'blue'] as const

// Fallback icon component
const FallbackIcon = Sparkles

interface BenefitsSectionProps {
  title: string
  sub_title: string
  benefits: ContentItem[]
}

export function BenefitsSection({
  title,
  sub_title,
  benefits,
}: BenefitsSectionProps) {
  const getIconComponent = (iconName?: string) => {
    if (!iconName) return FallbackIcon
    const IconComponent = (LucideIcons as any)[iconName]
    return IconComponent || FallbackIcon
  }

  return (
    <section className="py-12 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
            {sub_title}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const IconComponent = getIconComponent(benefit.icon)
            const glowColor = glowColors[index % glowColors.length]
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.3 }}
                viewport={{ once: true }}
                className="h-full"
              >
                <GlowCard
                  customSize={true}
                  className="p-8 h-full"
                  glowColor={glowColor}
                >
                  <div className="text-center flex flex-col items-center justify-center h-full">
                    <motion.div
                      className="flex items-center justify-center h-16 w-16 mx-auto bg-white/10 rounded-full"
                      whileHover={{ scale: 1.1, rotate: 10 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <IconComponent className="w-8 h-8 text-white" />
                    </motion.div>
                    {benefit.stat && (
                      <div className="mt-6 text-4xl font-bold text-white">
                        {benefit.stat}
                      </div>
                    )}
                    <h3 className="mt-4 text-xl font-semibold text-white">
                      {benefit.title}
                    </h3>
                    <p className="mt-2 text-base text-gray-400 max-w-xs mx-auto">
                      {benefit.description}
                    </p>
                  </div>
                </GlowCard>
              </motion.div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
